/**
 * StatusDetailDrawer - Drawer with detailed device/DSLR status
 * Shows connection status, device info, and DSLR info with expandable descriptions
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Tag, Collapse, Typography, Alert, Switch, Tabs, Button, App, Grid } from 'antd';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useNavigate } from 'react-router-dom';
import { AndroidStatus, CameraConfig, CameraMode, MobileConfig, GpsLocation, GpsError, View } from '@/types';
import { HistoryEventType } from '@/types/history';
import { HistoryTab } from '@/components/HistoryTab';
import { DrawerViewTitle } from '@/components/DrawerViewTitle';
import { useConnectionStatusFn, DEFAULT_HEARTBEAT_SECONDS } from '@/hooks/useConnectionStatus';
import { normalizeControllerName } from '@/utils/usbControllerNames';
import { MODAL_BLUR_STYLES } from '@/styles/modalStyles';
import { subscribeToMobileConfig } from '@/services/configService';
import { updateOtaAutoUpdate, requestReboot, sendRestartCommand, sendMasterPowerCommand, sendMasterPowerFirmwareUpdate, setMasterPowerRecoveryPaused } from '@/services/viewService';
import { useAuthStore } from '@/store/authStore';
import { subscribeToAdminStatus } from '@/services/userService';
import { StatusDashboardIcon } from './StatusDashboardIcon';
import {
  getMasterPowerStatus,
  MASTER_POWER_ICON_STATUS,
  MASTER_POWER_LABEL,
} from './masterPowerStatus';
import { getViewStatusItems, type StatusDisplayItem } from './statusItems';
import styles from './StatusDashboard.module.css';

const { Text } = Typography;

interface StatusDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  androidStatus: AndroidStatus | null | undefined;
  cameraSettings?: CameraConfig;
  cameraMode?: CameraMode;
  /** View token for connection status calculation */
  token?: string;
  /** GPS location data from view */
  gpsLocation?: GpsLocation;
  /** Last GPS error reported by device (used to distinguish failure modes) */
  gpsError?: GpsError;
  /** View ID for GPS map link */
  viewId?: string;
  /** Item ID to scroll to and highlight */
  scrollToItemId?: string | null;
  /** View identity for Firestore writes (userId + projectId + viewId) */
  userId?: string;
  projectId?: string;
  /** Current OTA auto-update setting */
  otaAutoUpdate?: boolean;
  /** Full view object for HistoryTab */
  view?: View | null;
  /** History filter state (lifted to parent for persistence) */
  historyFilterType: HistoryEventType | 'all';
  onHistoryFilterChange: (filter: HistoryEventType | 'all') => void;
}

/**
 * Format relative time (e.g., "2 min ago", "in 45s")
 */
function formatRelativeTime(ms: number, future = false): string {
  const absMs = Math.abs(ms);
  const seconds = Math.floor(absMs / 1000);

  if (seconds < 60) {
    return future ? `in ${seconds}s` : `${seconds}s ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return future ? `in ${minutes}m` : `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return future ? `in ${hours}h` : `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

export const StatusDetailDrawer: React.FC<StatusDetailDrawerProps> = ({
  open,
  onClose,
  androidStatus,
  cameraSettings,
  cameraMode,
  token,
  gpsLocation,
  gpsError,
  viewId,
  scrollToItemId,
  userId,
  projectId,
  otaAutoUpdate,
  view,
  historyFilterType,
  onHistoryFilterChange,
}) => {
  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const connectionStatusFn = useConnectionStatusFn();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const { user: authUser } = useAuthStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const [drawerTab, setDrawerTab] = useState('status');
  const [otaAutoUpdateSaving, setOtaAutoUpdateSaving] = useState(false);
  const [otaUpdateSending, setOtaUpdateSending] = useState(false);
  const [rebootSending, setRebootSending] = useState(false);
  const [mobileConfig, setMobileConfig] = useState<MobileConfig | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null);
  const [latestFirmware, setLatestFirmware] = useState<{ version: string; url: string; sha256?: string; releaseNotes?: string } | null>(null);
  const [fwUpdateSending, setFwUpdateSending] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    isOnline: boolean;
    isSurvival: boolean;
    lastSeenText: string;
    nextUpdateText: string;
  }>({ isOnline: false, isSurvival: false, lastSeenText: '—', nextUpdateText: '—' });

  useEffect(() => {
    if (!authUser) { setIsAdmin(false); return; }
    const unsub = subscribeToAdminStatus((s) => setIsAdmin(s), authUser.uid);
    return () => { unsub?.(); };
  }, [authUser]);

  // Subscribe to the latest firmware for THIS device's hardware generation.
  // Each generation has its own document because the pinouts differ — flashing a v3
  // binary onto a v2 board (or vice versa) bricks it functionally. A device that
  // reports no hardware revision is a v2: that firmware predates the DIS characteristic.
  const hardwareRevision = view?.masterPower?.hardwareRevision || 'v2';
  useEffect(() => {
    const firmwareDocId = hardwareRevision === 'v2'
      ? 'masterpower-firmware'
      : `masterpower-firmware-${hardwareRevision}`;
    // Clear while switching documents so a stale offer from the previous generation
    // cannot be shown against the new device.
    setLatestFirmware(null);
    const unsub = onSnapshot(doc(db, 'system', firmwareDocId), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setLatestFirmware({
          version: data.latestVersion,
          url: data.url,
          sha256: data.sha256,
          releaseNotes: data.releaseNotes,
        });
      } else {
        setLatestFirmware(null);
      }
    });
    return unsub;
  }, [hardwareRevision]);

  const handleFirmwareUpdate = useCallback(async () => {
    if (!userId || !projectId || !viewId || !latestFirmware) return;
    setFwUpdateSending(true);
    try {
      await sendMasterPowerFirmwareUpdate(userId, projectId, viewId, {
        url: latestFirmware.url,
        version: latestFirmware.version,
        sha256: latestFirmware.sha256,
      });
      message.success(`Firmware update ${latestFirmware.version} sent to device`);
    } catch (e) {
      message.error('Failed to send firmware update command');
    } finally {
      setFwUpdateSending(false);
    }
  }, [userId, projectId, viewId, latestFirmware, message]);

  // Subscribe to mobile config
  useEffect(() => {
    const unsubscribe = subscribeToMobileConfig((config) => {
      setMobileConfig(config);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (open) setDrawerTab('status');
  }, [open]);

  // Update connection info every second; the status itself comes from the shared hook
  useEffect(() => {
    if (!androidStatus?.lastSeen) {
      setConnectionInfo({ isOnline: false, isSurvival: false, lastSeenText: 'Never', nextUpdateText: '—' });
      return;
    }

    const updateInfo = () => {
      const status = connectionStatusFn(token, androidStatus);

      const isOnline = status === 'online';
      const isSurvival = status === 'survival';

      const diffMs = Date.now() - androidStatus.lastSeen!.toDate().getTime();
      const lastSeenText = formatRelativeTime(diffMs);

      let nextUpdateText = '—';
      if (isOnline) {
        const heartbeatMs = (mobileConfig?.heartbeatIntervalSeconds ?? DEFAULT_HEARTBEAT_SECONDS) * 1000;
        const nextUpdateMs = heartbeatMs - diffMs;
        if (nextUpdateMs > 0) {
          nextUpdateText = formatRelativeTime(nextUpdateMs, true);
        } else {
          nextUpdateText = 'any moment';
        }
      }

      setConnectionInfo({ isOnline, isSurvival, lastSeenText, nextUpdateText });
    };

    updateInfo();
    const interval = setInterval(updateInfo, 1000);
    return () => clearInterval(interval);
  }, [androidStatus?.lastSeen, androidStatus?.capturing?.reason, mobileConfig, token, connectionStatusFn]);

  // Handle scroll to item, expand accordion, and apply temporary highlight
  useEffect(() => {
    if (open && scrollToItemId) {
      // Expand the accordion for the target item
      setActiveKeys([scrollToItemId]);
      // Apply temporary highlight
      setHighlightedItemId(scrollToItemId);
      // Remove highlight after 2 seconds
      const timer = setTimeout(() => {
        setHighlightedItemId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [open, scrollToItemId]);

  // Scroll expanded item into view and apply highlight class
  useEffect(() => {
    if (activeKeys.length > 0 && highlightedItemId) {
      // Wait for collapse animation to complete
      setTimeout(() => {
        // Find the collapse item by its className
        const targetItem = document.querySelector(
          `.status-detail-collapse .status-item-${highlightedItemId}`
        );
        if (targetItem) {
          // Add highlight class
          targetItem.classList.add('status-item-highlight');
          // Scroll into view
          targetItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          // Add extra 16px scroll after scrollIntoView completes
          setTimeout(() => {
            const drawerBody = document.querySelector('.ant-drawer-body');
            if (drawerBody) {
              drawerBody.scrollBy({ top: 16, behavior: 'smooth' });
            }
          }, 400);
        }
      }, 350);
    }
  }, [activeKeys, highlightedItemId]);

  // Remove highlight class when highlightedItemId becomes null
  useEffect(() => {
    if (!highlightedItemId) {
      // Remove highlight class from all items
      document.querySelectorAll('.status-detail-collapse .status-item-highlight').forEach((el) => {
        el.classList.remove('status-item-highlight');
      });
    }
  }, [highlightedItemId]);

  const handleOtaAutoUpdateToggle = async (checked: boolean) => {
    if (!userId || !projectId || !viewId) return;
    setOtaAutoUpdateSaving(true);
    try {
      await updateOtaAutoUpdate(userId, projectId, viewId, checked);
    } finally {
      setOtaAutoUpdateSaving(false);
    }
  };

  const handleOtaUpdateNow = async () => {
    if (!userId || !projectId || !viewId) return;
    setOtaUpdateSending(true);
    try {
      await sendRestartCommand(userId, projectId, viewId);
      message.success('Update command sent');
    } catch {
      message.error('Failed to send update command');
    } finally {
      setOtaUpdateSending(false);
    }
  };

  const handleRebootNow = () => {
    if (!userId || !projectId || !viewId) return;
    modal.confirm({
      title: 'Reboot Device',
      content: 'The device will reboot immediately. Captures will resume automatically after restart.',
      okText: 'Reboot Now',
      okType: 'danger',
      styles: MODAL_BLUR_STYLES,
      onOk: async () => {
        setRebootSending(true);
        try {
          await requestReboot(userId, projectId, viewId);
          message.success('Reboot command sent');
        } catch {
          message.error('Failed to send reboot command');
        } finally {
          setRebootSending(false);
        }
      },
    });
  };

  // Status items per section, same composition as the cruscotto
  const { controllerItems, cameraItems } = useMemo(() =>
    getViewStatusItems({
      androidStatus,
      cameraSettings,
      cameraMode,
      gpsLocation,
      gpsError,
      isOnline: connectionInfo.isOnline,
      masterPower: view?.masterPower,
    }),
    [androidStatus, cameraSettings, cameraMode, gpsLocation, gpsError, connectionInfo.isOnline, view?.masterPower]
  );
  const showDslrSection = cameraMode === 'dslr';

  // Device info from androidStatus
  const device = androidStatus?.device;

  // Build a collapse entry for a status item
  const toCollapseItem = (item: StatusDisplayItem) => {
    // GPS item with position: make tagText a clickable link to map
    const isGpsWithPosition = item.id === 'gpsPosition' && gpsLocation && viewId;

    return {
      key: item.id,
      className: `status-item-${item.id}`,
      label: (
        <div className={styles.detailHeader}>
          <StatusDashboardIcon
            icon={item.icon}
            textIcon={item.textIcon}
            textIconStrike={item.textIconStrike}
            status={item.status}
          />
          <div className={styles.detailTextWrapper}>
            <span className={styles.detailTitle}>{item.label}</span>
            {isGpsWithPosition ? (
              <span
                className={styles.detailValue}
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  navigate(`/map?viewId=${viewId}`);
                }}
              >
                {item.tagText}
              </span>
            ) : (
              <span className={styles.detailValue}>{item.tagText}</span>
            )}
          </div>
        </div>
      ),
      children: (
        <div className={styles.detailDescription}>
          {item.description}
        </div>
      ),
    };
  };

  const controllerCollapseItems = controllerItems.map(toCollapseItem);
  const cameraCollapseItems = cameraItems.map(toCollapseItem);

  // Controller hardware (power board) card — admin-only diagnostics and controls.
  // In the External line the board is always part of the product, hence 'full_setup'.
  const mpStatus = getMasterPowerStatus(view?.masterPower, 'full_setup', cameraMode);
  const lastCycle = view?.masterPower?.lastCycle;
  const lastCycleText = lastCycle
    ? `${lastCycle.reason === 'manual' ? 'Manual' : 'Auto recovery'} — ${lastCycle.channel} — ${formatRelativeTime(Date.now() - lastCycle.timestamp.toDate().getTime())}`
    : null;

  const controllerHardwareCollapseItem = {
    key: 'controllerHardware',
    className: 'status-item-controllerHardware',
    label: (
      <div className={styles.detailHeader}>
        <StatusDashboardIcon
          icon="memory"
          status={MASTER_POWER_ICON_STATUS[mpStatus]}
        />
        <div className={styles.detailTextWrapper}>
          <span className={styles.detailTitle}>Power board</span>
          <span className={styles.detailValue}>{MASTER_POWER_LABEL[mpStatus]}</span>
        </div>
      </div>
    ),
    children: (() => {
      const rawFwVersion = view?.masterPower?.firmwareVersion;
      const lastOta = view?.masterPower?.lastOtaResult;
      const fwVersion = (lastOta?.success === true && lastOta.version && Number(lastOta.version) > Number(rawFwVersion))
        ? lastOta.version
        : rawFwVersion;
      const hasUpdate = fwVersion && latestFirmware && fwVersion !== latestFirmware.version;
      const otaInProgress = lastOta?.success === null;
      const pendingUpdate = view?.masterPower?.pendingFirmwareUpdate;

      return (
        <div className={styles.detailDescription}>
          {mpStatus === 'not_paired' && <div>No power board paired to this controller.</div>}
          {mpStatus === 'disconnected' && <div>Power board offline — check that it is powered on and within Bluetooth range.</div>}
          {fwVersion && (
            <div style={{ marginTop: 8 }}>
              <Text strong style={{ fontSize: 12 }}>Firmware{mpStatus === 'disconnected' ? ' (last seen)' : ''}</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                <Text strong style={{ fontSize: 14 }}>v{fwVersion}</Text>
                <Tag style={{ margin: 0 }}>HW {hardwareRevision}</Tag>
                {(otaInProgress || pendingUpdate) ? (
                  <Tag color="processing" style={{ margin: 0 }}>Updating...</Tag>
                ) : hasUpdate ? (
                  <Button
                    size="small"
                    type="primary"
                    loading={fwUpdateSending}
                    onClick={handleFirmwareUpdate}
                  >
                    Update to v{latestFirmware.version}
                  </Button>
                ) : (
                  <Tag color="success" variant="outlined" style={{ margin: 0 }}>Latest</Tag>
                )}
              </div>
              {lastOta?.success === false && (
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ✗ Update to v{lastOta.version} failed: {lastOta.message}
                  </Text>
                  {latestFirmware && !otaInProgress && !pendingUpdate && (
                    <Button
                      size="small"
                      loading={fwUpdateSending}
                      onClick={handleFirmwareUpdate}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
          {lastCycleText && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Last power cycle</Text>
              <div><Text style={{ fontSize: 13 }}>{lastCycleText}</Text></div>
            </div>
          )}
          {mpStatus === 'connected' && view && (
            <>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  icon={<MaterialIcon name="power_settings_new" />}
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'cycle_camera')}
                >
                  Cycle Camera
                </Button>
                <Button
                  size="small"
                  icon={<MaterialIcon name="power_settings_new" />}
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'cycle_hub')}
                >
                  Cycle Hub
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<MaterialIcon name="restart_alt" />}
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'reboot')}
                >
                  Reboot ESP32
                </Button>
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'hold_ch1_off')}
                >
                  CH1 OFF
                </Button>
                <Button
                  size="small"
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'hold_ch1_on')}
                >
                  CH1 ON
                </Button>
                <Button
                  size="small"
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'hold_ch2_off')}
                >
                  CH2 OFF
                </Button>
                <Button
                  size="small"
                  onClick={() => sendMasterPowerCommand(view.userId, view.projectId, view.viewId, 'hold_ch2_on')}
                >
                  CH2 ON
                </Button>
              </div>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                  size="small"
                  checked={view.masterPower?.recoveryPaused ?? false}
                  onChange={(checked) => setMasterPowerRecoveryPaused(view.userId, view.projectId, view.viewId, checked)}
                />
                <Text style={{ fontSize: 13 }}>Pause auto recovery</Text>
              </div>
            </>
          )}
        </div>
      );
    })(),
  };

  return (
    <Drawer
      rootClassName="drawer-dark-header"
      title={<DrawerViewTitle view={view} />}
      placement="right"
      width={isMobile ? '100%' : 360}
      open={open}
      onClose={onClose}
      closeIcon={null}
      extra={
        <Button
          type="default"
          icon={<MaterialIcon name="close" size={20} />}
          onClick={onClose}
        />
      }
      styles={MODAL_BLUR_STYLES}
    >
      <Tabs
        activeKey={drawerTab}
        onChange={setDrawerTab}
        items={[
          {
            key: 'status',
            label: 'Status',
            icon: <MaterialIcon name="checklist" size={22} />,
            children: (
              <div className={styles.drawerContent}>
                {/* Connection Status Header */}
                <div className={styles.connectionHeader}>
                  <MaterialIcon
                    name={connectionInfo.isSurvival ? 'battery_saver' : connectionInfo.isOnline ? 'cloud_done' : 'cloud_off'}
                    size={28}
                    style={{ color: connectionInfo.isSurvival ? '#faad14' : connectionInfo.isOnline ? '#52c41a' : '#ff4d4f' }}
                  />
                  <div className={styles.connectionStatus}>
                    <div className={connectionInfo.isSurvival ? styles.connectionSurvival : connectionInfo.isOnline ? styles.connectionOnline : styles.connectionOffline}>
                      {connectionInfo.isSurvival ? 'Survival mode' : connectionInfo.isOnline ? 'Online' : 'Offline'}
                    </div>
                    <div className={styles.connectionTime}>
                      Last seen: {connectionInfo.lastSeenText}
                      {connectionInfo.isOnline && ` • Next: ${connectionInfo.nextUpdateText}`}
                    </div>
                    {connectionInfo.isSurvival && (
                      <div className={styles.connectionTime} style={{ marginTop: 2 }}>
                        {`Capturing paused — battery below survival threshold (${mobileConfig?.survivalBatteryPercent ?? 10}%) and charger disconnected. The device sends a status update every hour. Everything resumes automatically when power is restored. Survival mode prevents full battery drain, which on some devices causes a permanent shutdown that requires manual intervention to restart.`}
                      </div>
                    )}
                  </div>
                </div>

                {/* Offline warning banner */}
                {!connectionInfo.isOnline && !connectionInfo.isSurvival && androidStatus?.lastSeen && (
                  <Alert
                    type="warning"
                    showIcon
                    icon={<MaterialIcon name="warning" />}
                    message="Data from last update — device currently offline"
                    style={{ marginBottom: 8 }}
                  />
                )}

                {/* Controller (External) / capture unit (Built-in) section */}
                <div className={styles.drawerSection}>
                  <div className={styles.drawerSectionTitle}>{showDslrSection ? 'Controller' : 'Capture unit'}</div>

                  <Collapse
                    items={controllerCollapseItems}
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(keys as string[])}
                    accordion
                    expandIconPosition="end"
                    ghost
                    className="status-detail-collapse"
                  />
                </div>

                {/* Controller hardware — admin only (isAdmin follows the logged-in user,
                    so it also shows while impersonating). External line only. */}
                {showDslrSection && isAdmin && (
                  <div className={styles.drawerSection}>
                    <div className={styles.drawerSectionTitle}>Controller hardware (admin)</div>

                    <Collapse
                      items={[controllerHardwareCollapseItem]}
                      activeKey={activeKeys}
                      onChange={(keys) => setActiveKeys(keys as string[])}
                      accordion
                      expandIconPosition="end"
                      ghost
                      className="status-detail-collapse"
                    />
                  </div>
                )}

                {/* External camera section */}
                {showDslrSection && (
                  <div className={styles.drawerSection}>
                    <div className={styles.drawerSectionTitle}>External Camera</div>

                    <Collapse
                      items={cameraCollapseItems}
                      activeKey={activeKeys}
                      onChange={(keys) => setActiveKeys(keys as string[])}
                      accordion
                      expandIconPosition="end"
                      ghost
                      className="status-detail-collapse"
                    />
                  </div>
                )}

                {/* Additional device details */}
                {device && (
                  <div className={styles.drawerSection}>
                    <div className={styles.drawerSectionTitle}>Storage & System</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {/* Mode */}
                      <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Device Mode</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Text strong style={{ fontSize: 14 }}>{device.deviceOwner ? 'Production Installation' : 'Test Installation'}</Text>
                          {device.deviceOwner ? (
                            <Tag color="success" variant="outlined" style={{ margin: 0 }}>Full control</Tag>
                          ) : (
                            <Tag color="warning" variant="outlined" style={{ margin: 0 }}>Limited control</Tag>
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                          {device.deviceOwner
                            ? 'Full device management: silent app updates, kiosk lock, remote reboot, USB auto-permissions.'
                            : (<>Limited mode: no silent updates, no kiosk lock, no remote reboot. See{' '}
                                <a href="https://masterlapse.com/get-started/" target="_blank" rel="noopener noreferrer">
                                  Production installation
                                </a>
                                {' '}for full control.</>)}
                        </Text>
                      </div>

                      {/* App Version (test installation — no auto-update toggle) */}
                      {!device.deviceOwner && device.appVersionName && (() => {
                        const deviceVersion = device.appVersionCode || 0;
                        const latestVersion = mobileConfig?.latestAppVersionCode || 0;
                        const isOutdated = latestVersion > 0 && deviceVersion < latestVersion;
                        return (
                          <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>App Version</Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Text strong style={{ fontSize: 14 }}>v{device.appVersionName}</Text>
                              {isOutdated ? (
                                <Tag color="warning" variant="outlined" style={{ margin: 0 }}>Update available</Tag>
                              ) : (
                                <Tag color="success" variant="outlined" style={{ margin: 0 }}>Latest</Tag>
                              )}
                            </div>
                            {isOutdated && (
                              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                                A new version is available. On the device, tap ⋮ menu → Update to install.
                              </Text>
                            )}
                          </div>
                        );
                      })()}

                      {/* Auto-Update toggle with version info (production installation only) */}
                      {device.deviceOwner && userId && projectId && viewId && (() => {
                        const otaOff = (otaAutoUpdate ?? true) === false;
                        const deviceVer = device.appVersionCode || 0;
                        const latestVer = mobileConfig?.latestAppVersionCode || 0;
                        const updateAvailable = latestVer > 0 && deviceVer < latestVer;
                        return (
                          <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                            {/* Row 1: Version + tag */}
                            <Text type="secondary" style={{ fontSize: 12 }}>App Version</Text>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Text strong style={{ fontSize: 14 }}>v{device.appVersionName ?? '—'}</Text>
                              {updateAvailable ? (
                                <Tag color="warning" variant="outlined" style={{ margin: 0 }}>Update available</Tag>
                              ) : (
                                <Tag color="success" variant="outlined" style={{ margin: 0 }}>Latest</Tag>
                              )}
                            </div>
                            {/* Row 2: Auto-Update toggle + Update Now button */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>Auto-Update</Text>
                              <Switch
                                checked={otaAutoUpdate ?? true}
                                onChange={handleOtaAutoUpdateToggle}
                                loading={otaAutoUpdateSaving}
                                size="small"
                              />
                              {otaOff && updateAvailable && (
                                <Button
                                  size="small"
                                  type="primary"
                                  icon={<MaterialIcon name="system_update" size={14} />}
                                  loading={otaUpdateSending}
                                  disabled={!connectionInfo.isOnline}
                                  onClick={handleOtaUpdateNow}
                                  style={{ marginLeft: 'auto' }}
                                >
                                  Update Now
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Device Storage */}
                      <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Device Storage</Text>
                        <div>
                          <Text strong>{device.storageUsedGB?.toFixed(1) ?? '—'} / {device.storageTotalGB?.toFixed(0) ?? '—'} GB</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                          Storage is only relevant when the device goes offline — photos are kept locally and uploaded when connectivity is restored.
                        </Text>
                      </div>

                      {/* Timezone */}
                      {device.timezone && (
                        <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Timezone</Text>
                          <div>
                            <Text strong>{device.timezone}</Text>
                          </div>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                            Photo timestamps and capture schedules use this local timezone.
                          </Text>
                        </div>
                      )}

                      {/* USB Controller (phone's built-in host controller) */}
                      {device?.usb?.controller && (
                        <div className={styles.detailCard} style={{ gridColumn: 'span 2' }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>Phone USB Host Controller</Text>
                          <div><Text strong>{normalizeControllerName(device.usb.controller)}</Text></div>
                          <div style={{ marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Using a DSLR or mirrorless camera? Find the right USB hub for this controller on the{' '}
                              <a href="https://masterlapse.com/usb-compatibility" target="_blank" rel="noopener noreferrer">
                                USB compatibility page
                              </a>.
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Recovery — requires Device Owner for reboot privilege */}
                {device?.deviceOwner && userId && projectId && viewId && (
                  <div className={styles.drawerSection}>
                    <div className={styles.drawerSectionTitle}>Recovery</div>
                    <div className={styles.detailCard}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <Text strong>Reboot Now</Text>
                        </div>
                        <Button
                          size="small"
                          danger
                          icon={<MaterialIcon name="restart_alt" size={14} />}
                          loading={rebootSending}
                          disabled={!connectionInfo.isOnline}
                          onClick={handleRebootNow}
                        >
                          Reboot
                        </Button>
                      </div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                        Force an immediate device reboot. Use this when the camera is stuck and auto-recovery
                        hasn't kicked in yet. Captures will resume automatically after restart.
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'history',
            label: 'History',
            icon: <MaterialIcon name="history" size={22} />,
            children: view ? (
              <HistoryTab
                view={view}
                filterType={historyFilterType}
                onFilterChange={onHistoryFilterChange}
              />
            ) : null,
          },
        ]}
      />
    </Drawer>
  );
};
